import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import { DndContext, useDroppable, useDraggable } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { useNavigate } from "react-router-dom";

export const PhaseReorder = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate(); // Hook to navigate to the previous page

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch phases data from API
  useEffect(() => {
    const fetchPhasesData = async () => {
      try {
        const response = await fetch(`${API_URL.GET_PHASE_ALL}?page=1&limit=50`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch phases.');
        }
        const data = await response.json();
        setItems(data.phases);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPhasesData();
  }, [staff.token]);

  // Function to update the order in the database
  const updateOrderInDatabase = async (newOrder) => {
    try {
      const response = await fetch(API_URL.UPDATE_PHASE_ORDER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ phases: newOrder }),
      });
      if (!response.ok) {
        throw new Error('Failed to update order in database.');
      }
      setSuccess('Order updated successfully.');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle drag end logic
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedItems = arrayMove(items, oldIndex, newIndex);
        setItems(updatedItems);
        updateOrderInDatabase(updatedItems);
      }
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16">
        {/* Go Back Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate(-1)} // Go back to the previous page
            className="px-4 py-2 bg-red-500 text-white font-semibold rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Go Back
          </button>
        </div>

        {/* Instructional Note */}
        <div className="mb-4 p-4 bg-blue-100 text-blue-700 border-l-4 border-blue-500 rounded-md">
  <strong>Drag and Drop Instructions:</strong> You can reorder the phases by dragging and dropping the items below. Simply click and drag a phase to its new position.
</div>

        <h1 className="text-2xl font-bold mb-6">Reorder Phases</h1>

        {/* Loading Indicator */}
        {loading && (
          <div className="mb-4 p-4 text-blue-700 bg-blue-100 rounded">
            Loading phases...
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 rounded">
            Error: {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 text-green-700 bg-green-100 rounded">
            {success}
          </div>
        )}

        {/* Drag and Drop Context */}
        {!loading && !error && (
          <DndContext onDragEnd={handleDragEnd}>
            <div className="space-y-4">
              {items.map((item) => (
                <Droppable key={item._id} id={item._id}>
                  <Draggable id={item._id}>
                    <div className="p-4 border rounded bg-white shadow-sm">
                      {item.title}
                    </div>
                  </Draggable>
                </Droppable>
              ))}
            </div>
          </DndContext>
        )}
      </div>
    </div>
  );
};

// Draggable Component
const Draggable = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

// Droppable Component
const Droppable = ({ id, children }) => {
  const { setNodeRef } = useDroppable({
    id,
  });

  return (
    <div ref={setNodeRef}>
      {children}
    </div>
  );
};
